import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LifeBuoy, Loader2, Send } from 'lucide-react';

const STATUS_CLS = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-slate-100 text-slate-700',
};

export default function AdminSupportRequests() {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/support-requests'); setRows(r.data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/support-requests/${sel.id}/messages`, { body: reply.trim() });
      setReply('');
      const r = await api.get('/admin/support-requests');
      setRows(r.data || []);
      setSel((r.data || []).find((x) => x.id === sel.id) || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/support-requests/${sel.id}`, { status });
      toast.success(L('Estado actualizado', 'Status updated'));
      const r = await api.get('/admin/support-requests');
      setRows(r.data || []);
      setSel((r.data || []).find((x) => x.id === sel.id) || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Topbar
        title={L('Solicitudes de apoyo', 'Support requests')}
        subtitle={L('Solicitudes de apoyo profesional enviadas por los clientes — asesoría, no aprobación',
          'Client requests for professional support — advisory, never an approval gate')}
      />
      <div className="p-6 lg:p-8 grid lg:grid-cols-5 gap-4" data-testid="admin-support-requests">
        <Card className="lg:col-span-2 shadow-card overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground" data-testid="support-empty">
              {L('Sin solicitudes de apoyo.', 'No support requests yet.')}
            </div>
          ) : (
            <ul className="divide-y divide-border max-h-[70vh] overflow-y-auto">
              {rows.map((r) => (
                <li key={r.id}
                    className={`px-4 py-3 cursor-pointer hover:bg-muted/40 ${sel?.id === r.id ? 'bg-muted/50' : ''}`}
                    onClick={() => setSel(r)} data-testid={`support-row-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <LifeBuoy className="h-3.5 w-3.5 text-primary shrink-0" />
                    <p className="text-sm font-medium truncate flex-1">{r.subject}</p>
                    <Badge variant="outline" className={`text-[10px] ${STATUS_CLS[r.status] || ''}`}>{r.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {r.company_name} · <span className="capitalize">{r.suite}</span> · {r.requested_by_name}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="lg:col-span-3 shadow-card p-5">
          {!sel ? (
            <p className="text-sm text-muted-foreground py-10 text-center">
              {L('Seleccione una solicitud para ver el detalle.', 'Select a request to view its detail.')}
            </p>
          ) : (
            <div className="space-y-3" data-testid="support-detail">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold">{sel.subject}</h3>
                  <p className="text-xs text-muted-foreground">
                    {sel.company_name} · <span className="capitalize">{sel.suite}</span> · {sel.category}
                    {sel.entity_ref?.label && <> · {sel.entity_ref.label}</>}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" disabled={busy || sel.status === 'in_progress'}
                          onClick={() => setStatus('in_progress')} data-testid="support-status-progress">
                    {L('En curso', 'In progress')}
                  </Button>
                  <Button size="sm" disabled={busy || sel.status === 'resolved'}
                          onClick={() => setStatus('resolved')} data-testid="support-status-resolved">
                    {L('Resuelta', 'Resolved')}
                  </Button>
                </div>
              </div>
              <div className="space-y-2 max-h-[42vh] overflow-y-auto rounded-md border border-border p-3">
                {(sel.messages || []).map((m) => (
                  <div key={m.id} className={`text-sm rounded-md p-2 ${m.sender_role?.startsWith('hm') ? 'bg-primary/5 ml-6' : 'bg-muted/50 mr-6'}`}>
                    <p className="text-[11px] font-semibold text-muted-foreground">{m.sender_name}</p>
                    <p className="whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
                          placeholder={L('Responder al cliente…', 'Reply to the client…')} data-testid="support-reply-input" />
                <Button onClick={sendReply} disabled={busy || !reply.trim()} data-testid="support-reply-send">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
